/**
 * FilteringRuleDialog - dedicated editor for warranty-wide filtering rules.
 *
 * Authors `AvailabilityRule` records with `scope: 'warranty_filter'`.
 * Conditions describe *when* the rule fires (matched against registration
 * context), and `query_filters` describe *what* warranty templates to keep
 * (categories, types, specific templates, warranty attribute equality).
 *
 * Design intent
 *   • Sectioned layout following a "WHEN → THEN" mental model so admins
 *     understand they are authoring a filter, not an effect override.
 *   • Compact pill-style multi-selects in place of long checkbox columns -
 *     scannable, less chrome, fewer pixels per choice.
 *   • Schedule + severity pulled into a quieter "Settings" section so the
 *     primary attention stays on conditions and targets.
 */

import { useMemo, useState } from 'react'
import { ArrowRight, Filter, Plus, Settings2, Trash2, Zap } from 'lucide-react'
import { toast } from 'sonner'
import { toastApiError } from '@/shared/lib/api-error'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { Button } from '@/shared/components/ui/button'
import { DatePicker } from '@/shared/components/ui/date-picker'
import { Switch } from '@/shared/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogBody,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/shared/components/ui/select'
import { ConditionBuilder } from '@/admin/components/condition-builder'
import { useAvailableRefs } from '@/admin/components/condition-builder/use-available-refs'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import {
  useCreateAvailabilityRule,
  useUpdateAvailabilityRule,
  type AvailabilityQueryFilters,
  type AvailabilityRule,
} from '@/admin/hooks/use-availability-rules'
import type { Severity } from '@/admin/hooks/use-validation-rules'
import type {
  WarrantyAttribute,
  WarrantyCategory,
  WarrantyTemplate,
} from '@/admin/types/warranties'
import { cn } from '@/shared/lib/utils'

const SEVERITIES: { value: Severity; label: string; hint: string }[] = [
  { value: 'BLOCK', label: 'Block', hint: 'Hide non-matching warranties' },
  { value: 'WARN', label: 'Warn', hint: 'Show with a warning' },
  { value: 'INFO', label: 'Info', hint: 'Informational only' },
]

const WARRANTY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'MANUFACTURER_WARRANTY', label: 'Standard' },
  { value: 'RETAILER_WARRANTY', label: 'Retailer' },
  { value: 'EXTENDED_WARRANTY', label: 'Extended' },
]

interface FormState {
  name: string
  description: string
  conditions: PredicateNode | null
  severity: Severity
  message: string
  effective_from: string
  effective_until: string
  is_active: boolean
  query_filters: AvailabilityQueryFilters
}

const EMPTY: FormState = {
  name: '',
  description: '',
  conditions: null,
  severity: 'BLOCK',
  message: '',
  effective_from: '',
  effective_until: '',
  is_active: true,
  query_filters: {},
}

function toggleStringInArray(
  values: string[] | undefined,
  value: string,
): string[] {
  const current = new Set(values ?? [])
  if (current.has(value)) current.delete(value)
  else current.add(value)
  return [...current]
}

// ── Subcomponents ────────────────────────────────────────────

function FormSection({
  icon: Icon,
  eyebrow,
  title,
  hint,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  eyebrow: string
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-start gap-2.5">
        <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-2)]">
          <Icon className="h-3 w-3" />
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-[9px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {eyebrow}
          </span>
          <span className="text-[14px] font-medium leading-tight text-[var(--ink)]">
            {title}
          </span>
          {hint ? (
            <span className="text-[11px] leading-snug text-[var(--ink-3)]">
              {hint}
            </span>
          ) : null}
        </div>
      </header>
      <div className="pl-[34px]">{children}</div>
    </section>
  )
}

function ChipToggle({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-full border-[0.5px] px-3 text-[12px] transition-all duration-150',
        active
          ? 'border-[var(--green)] bg-[var(--green)] text-[var(--bg)] shadow-[0_1px_0_rgba(0,0,0,0.04)]'
          : 'border-[var(--rule)] bg-transparent text-[var(--ink-2)] hover:border-[var(--rule-strong)] hover:text-[var(--ink)]',
      )}
    >
      {active ? (
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--bg)]" />
      ) : (
        <span className="inline-block h-1.5 w-1.5 rounded-full border-[0.5px] border-[var(--ink-3)]" />
      )}
      {children}
    </button>
  )
}

function ChipGroup({
  label,
  count,
  children,
  empty,
}: {
  label: string
  count?: number
  children: React.ReactNode
  empty?: boolean
}) {
  return (
    <div>
      <div className="mb-2 flex items-baseline gap-2">
        <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
          {label}
        </span>
        {typeof count === 'number' && count > 0 ? (
          <span className="text-[10px] tabular-nums text-[var(--ink-3)]">
            {count} selected
          </span>
        ) : null}
      </div>
      {empty ? (
        <p className="text-[12px] text-[var(--ink-3)]">No options available.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">{children}</div>
      )}
    </div>
  )
}

// ── Main component ───────────────────────────────────────────

export function FilteringRuleDialog({
  attributes,
  warrantyTemplates = [],
  warrantyCategories = [],
  open,
  onOpenChange,
  editing,
}: {
  attributes: WarrantyAttribute[]
  warrantyTemplates?: WarrantyTemplate[]
  warrantyCategories?: WarrantyCategory[]
  open: boolean
  onOpenChange: (open: boolean) => void
  editing: AvailabilityRule | null
}) {
  const createRule = useCreateAvailabilityRule()
  const updateRule = useUpdateAvailabilityRule()
  const { refs, byRef, isLoading: refsLoading } = useAvailableRefs()
  const isEdit = !!editing

  const [form, setForm] = useState<FormState>(EMPTY)
  const [seedKey, setSeedKey] = useState<string>('')
  const [showSettings, setShowSettings] = useState(false)
  const nextKey = open ? (editing?.uuid ?? 'new') : '__closed__'
  if (nextKey !== seedKey) {
    setSeedKey(nextKey)
    if (!open) {
      setForm(EMPTY)
      setShowSettings(false)
    } else if (editing) {
      setForm({
        name: editing.name,
        description: editing.description ?? '',
        conditions: editing.conditions,
        severity: editing.severity,
        message: editing.message ?? '',
        effective_from: editing.effective_from ?? '',
        effective_until: editing.effective_until ?? '',
        is_active: editing.is_active,
        query_filters: editing.query_filters ?? {},
      })
      setShowSettings(
        Boolean(
          editing.effective_from ||
            editing.effective_until ||
            editing.message ||
            !editing.is_active ||
            editing.severity !== 'BLOCK',
        ),
      )
    } else {
      setForm(EMPTY)
      setShowSettings(false)
    }
  }

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function updateQueryFilters(next: Partial<AvailabilityQueryFilters>) {
    setField('query_filters', { ...form.query_filters, ...next })
  }

  function updateAttributeFilter(
    index: number,
    next: { code_name?: string; value?: unknown },
  ) {
    const filters = [...(form.query_filters.attribute_filters ?? [])]
    const current = filters[index] ?? { code_name: '', value: '' }
    filters[index] = { ...current, ...next }
    updateQueryFilters({ attribute_filters: filters })
  }

  function removeAttributeFilter(index: number) {
    updateQueryFilters({
      attribute_filters: (form.query_filters.attribute_filters ?? []).filter(
        (_entry, entryIndex) => entryIndex !== index,
      ),
    })
  }

  const targetCount = useMemo(() => {
    const f = form.query_filters
    return (
      (f.deny_all ? 1 : 0) +
      (f.warranty_types?.length ?? 0) +
      (f.category_ids?.length ?? 0) +
      (f.template_ids?.length ?? 0) +
      (f.attribute_filters?.length ?? 0)
    )
  }, [form.query_filters])

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error('Rule name is required')
      return
    }
    if (targetCount === 0) {
      toast.error('Add at least one target so the rule narrows something')
      return
    }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      conditions: form.conditions,
      severity: form.severity,
      message: form.message.trim() || null,
      scope: 'warranty_filter' as const,
      scope_id: null,
      effect_overrides: {},
      query_filters: form.query_filters,
      effective_from: form.effective_from || null,
      effective_until: form.effective_until || null,
      is_active: form.is_active,
    }
    try {
      if (isEdit && editing) {
        await updateRule.mutateAsync({ uuid: editing.uuid, ...payload })
        toast.success('Rule updated')
      } else {
        await createRule.mutateAsync(payload)
        toast.success('Rule created')
      }
      setForm(EMPTY)
      onOpenChange(false)
    } catch (err) {
      toastApiError(
        err,
        isEdit ? 'Failed to update rule' : 'Failed to create rule',
      )
    }
  }

  const isPending = createRule.isPending || updateRule.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        bare
        className="sm:max-w-[680px] max-h-[min(86vh,900px)]"
      >
        <DialogHeader className="flex flex-row items-start gap-3 border-b-[0.5px] border-[var(--rule)] px-6 pt-5 pb-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-2)]">
            <Filter className="h-4 w-4" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <DialogTitle className="text-[16px] leading-tight">
              {isEdit ? 'Edit rule' : 'Add rule'}
            </DialogTitle>
            <DialogDescription className="text-[12px] leading-snug text-[var(--ink-3)]">
              When the conditions match registration data, narrow which
              warranties appear in the next step of the registration flow.
            </DialogDescription>
          </div>
        </DialogHeader>

        <DialogBody className="gap-6">
          {/* Rule basics */}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Rule name
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="e.g. Detached homes - show platinum warranties"
                className="h-9 text-[13px]"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Description
              </Label>
              <Textarea
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="Optional admin note explaining why this rule exists"
                className="min-h-[64px] text-[13px]"
              />
            </div>
          </div>

          <div className="-mx-6 border-t-[0.5px] border-[var(--rule)]" />

          {/* WHEN - conditions */}
          <FormSection
            icon={Zap}
            eyebrow="When"
            title="Match these registration conditions"
            hint="All conditions must be met for the rule to apply. Reference any registration field."
          >
            {refsLoading ? (
              <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-4 text-center text-[12px] text-[var(--ink-3)]">
                Loading fields…
              </div>
            ) : (
              <ConditionBuilder
                value={form.conditions}
                onChange={(next) => setField('conditions', next)}
                availableRefs={refs}
                byRef={byRef}
              />
            )}
          </FormSection>

          {/* Visual flow connector */}
          <div className="flex items-center gap-2 pl-[34px] text-[var(--ink-3)]">
            <div className="h-[0.5px] flex-1 bg-[var(--rule)]" />
            <ArrowRight className="h-3 w-3" />
            <div className="h-[0.5px] flex-1 bg-[var(--rule)]" />
          </div>

          {/* THEN - targets */}
          <FormSection
            icon={Filter}
            eyebrow="Then"
            title="Narrow warranties to"
            hint="Combine targets to define what to keep. Empty groups don't filter."
          >
            <div className="flex flex-col gap-5 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]/40 p-4">
              <ChipGroup
                label="Warranty types"
                count={form.query_filters.warranty_types?.length}
              >
                {WARRANTY_TYPE_OPTIONS.map((opt) => {
                  const active = (form.query_filters.warranty_types ?? []).includes(
                    opt.value,
                  )
                  return (
                    <ChipToggle
                      key={opt.value}
                      active={active}
                      onClick={() =>
                        updateQueryFilters({
                          warranty_types: toggleStringInArray(
                            form.query_filters.warranty_types,
                            opt.value,
                          ),
                        })
                      }
                    >
                      {opt.label}
                    </ChipToggle>
                  )
                })}
              </ChipGroup>

              <ChipGroup
                label="Categories"
                count={form.query_filters.category_ids?.length}
                empty={warrantyCategories.length === 0}
              >
                {warrantyCategories.map((category) => {
                  const active = (form.query_filters.category_ids ?? []).includes(
                    category.uuid,
                  )
                  return (
                    <ChipToggle
                      key={category.uuid}
                      active={active}
                      onClick={() =>
                        updateQueryFilters({
                          category_ids: toggleStringInArray(
                            form.query_filters.category_ids,
                            category.uuid,
                          ),
                        })
                      }
                    >
                      {category.name}
                    </ChipToggle>
                  )
                })}
              </ChipGroup>

              <ChipGroup
                label="Specific templates"
                count={form.query_filters.template_ids?.length}
                empty={warrantyTemplates.length === 0}
              >
                {warrantyTemplates.map((template) => {
                  const active = (form.query_filters.template_ids ?? []).includes(
                    template.uuid,
                  )
                  return (
                    <ChipToggle
                      key={template.uuid}
                      active={active}
                      onClick={() =>
                        updateQueryFilters({
                          template_ids: toggleStringInArray(
                            form.query_filters.template_ids,
                            template.uuid,
                          ),
                        })
                      }
                    >
                      {template.name}
                    </ChipToggle>
                  )
                })}
              </ChipGroup>

              <ChipGroup label="Outcome" count={form.query_filters.deny_all ? 1 : 0}>
                <ChipToggle
                  active={form.query_filters.deny_all === true}
                  onClick={() =>
                    updateQueryFilters({
                      deny_all: form.query_filters.deny_all ? undefined : true,
                    })
                  }
                >
                  Hide all warranties
                </ChipToggle>
              </ChipGroup>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    Attribute filters
                  </span>
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() =>
                      updateQueryFilters({
                        attribute_filters: [
                          ...(form.query_filters.attribute_filters ?? []),
                          { code_name: '', value: '' },
                        ],
                      })
                    }
                    disabled={attributes.length === 0}
                    className="h-7 px-2 text-[11px] text-[var(--ink-2)] hover:bg-[var(--bg)]"
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Add filter
                  </Button>
                </div>
                {(form.query_filters.attribute_filters ?? []).length === 0 ? (
                  <p className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg)] px-3 py-2.5 text-[12px] text-[var(--ink-3)]">
                    {attributes.length === 0
                      ? 'No warranty attributes defined yet.'
                      : 'Add a row to filter by a custom warranty attribute value.'}
                  </p>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    {(form.query_filters.attribute_filters ?? []).map(
                      (filter, index) => (
                        <div
                          key={`attr-${index}`}
                          className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-1.5 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-1.5"
                        >
                          <Select
                            value={filter.code_name}
                            onValueChange={(value: string) =>
                              updateAttributeFilter(index, { code_name: value })
                            }
                          >
                            <SelectTrigger className="h-8 border-0 bg-transparent px-2 text-[13px] focus-visible:ring-0">
                              <SelectValue placeholder="Choose attribute" />
                            </SelectTrigger>
                            <SelectContent>
                              {attributes.map((attribute) => (
                                <SelectItem
                                  key={attribute.uuid}
                                  value={attribute.code_name}
                                >
                                  {attribute.display_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            value={String(filter.value ?? '')}
                            onChange={(e) =>
                              updateAttributeFilter(index, { value: e.target.value })
                            }
                            placeholder="equals…"
                            className="h-8 border-0 bg-transparent text-[13px] focus-visible:ring-0"
                          />
                          <Button
                            variant="ghost"
                            type="button"
                            onClick={() => removeAttributeFilter(index)}
                            className="h-7 w-7 p-0 text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ),
                    )}
                  </div>
                )}
              </div>
            </div>
          </FormSection>

          <div className="-mx-6 border-t-[0.5px] border-[var(--rule)]" />

          {/* Settings */}
          <section className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => setShowSettings((v) => !v)}
              className="flex items-center gap-2 self-start text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink-3)] transition-colors hover:text-[var(--ink)]"
            >
              <Settings2 className="h-3 w-3" />
              {showSettings ? 'Hide settings' : 'Show settings'}
            </button>

            {showSettings ? (
              <div className="flex flex-col gap-4 rounded-[8px] border-[0.5px] border-[var(--rule)] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-col">
                    <span className="text-[13px] text-[var(--ink)]">
                      Rule active
                    </span>
                    <span className="text-[11px] text-[var(--ink-3)]">
                      When off, this rule is ignored without deleting it.
                    </span>
                  </div>
                  <Switch
                    checked={form.is_active}
                    aria-label="Toggle rule active"
                    onCheckedChange={(checked) => setField('is_active', checked)}
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                      Effective from
                    </Label>
                    <DatePicker
                      value={form.effective_from}
                      onChange={(value) => setField('effective_from', value)}
                      placeholder="No start date"
                      className="text-[13px]"
                    />
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                      Effective until
                    </Label>
                    <DatePicker
                      value={form.effective_until}
                      onChange={(value) => setField('effective_until', value)}
                      placeholder="No end date"
                      className="text-[13px]"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    Severity
                  </Label>
                  <div className="flex flex-wrap gap-1.5">
                    {SEVERITIES.map((s) => {
                      const active = form.severity === s.value
                      return (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => setField('severity', s.value)}
                          className={cn(
                            'flex flex-col items-start gap-0.5 rounded-[6px] border-[0.5px] px-3 py-2 text-left transition-all duration-150',
                            active
                              ? 'border-[var(--green)] bg-[var(--green)]/5 text-[var(--ink)]'
                              : 'border-[var(--rule)] hover:border-[var(--rule-strong)]',
                          )}
                        >
                          <span className="text-[12px] font-medium leading-tight">
                            {s.label}
                          </span>
                          <span className="text-[10px] leading-tight text-[var(--ink-3)]">
                            {s.hint}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div>
                  <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    Message
                  </Label>
                  <Input
                    value={form.message}
                    onChange={(e) => setField('message', e.target.value)}
                    placeholder="Shown when the rule fires (optional)"
                    className="h-9 text-[13px]"
                  />
                </div>
              </div>
            ) : null}
          </section>
        </DialogBody>

        <DialogFooter className="flex items-center justify-between gap-2 border-t-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]/40 px-6 py-3 sm:flex-row sm:justify-between">
          <p className="text-[11px] text-[var(--ink-3)]">
            {targetCount === 0
              ? 'Add at least one target to save.'
              : `${targetCount} target${targetCount === 1 ? '' : 's'} configured`}
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-9 px-3 text-[12px]"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isPending}
              className="h-9 cursor-pointer rounded-[6px] bg-[var(--green)] px-4 text-[12px] font-medium text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-40"
            >
              {isEdit ? 'Save changes' : 'Create rule'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default FilteringRuleDialog
